/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import React from 'react';
import { Card, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ProfileDetailItem } from '../../services/statistics.service';

const { Title, Text } = Typography;

interface ProfileDetailTableProps {
  target?: 'user' | 'relative' | 'all';
  data: ProfileDetailItem[];
  loading: boolean;
  totalRows: number;
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number, pageSize: number) => void;
}

export const ProfileDetailTable: React.FC<ProfileDetailTableProps> = ({
  target = 'user',
  data,
  loading,
  totalRows,
  currentPage,
  pageSize,
  onPageChange,
}) => {

  const userColumns: ColumnsType<ProfileDetailItem> = [
    {
      title: 'User ID',
      dataIndex: 'userId',
      key: 'userId',
      render: val => val || '',
    },
    {
      title: 'Họ và Tên',
      dataIndex: 'fullName',
      key: 'fullName',
      render: val => val || '',
    },
    {
      title: 'Số điện thoại',
      dataIndex: 'phone',
      key: 'phone',
      render: val => val || '',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: val => val || '',
    },
    {
      title: 'Ngày sinh',
      dataIndex: 'dob',
      key: 'dob',
      render: val => val || '',
    },
    {
      title: 'Giới tính',
      dataIndex: 'gender',
      key: 'gender',
      render: val => val || '',
    },
    {
      title: 'Tỉnh thành',
      dataIndex: 'province',
      key: 'province',
      render: val => val || '',
    },
    {
      title: 'Độ tuổi',
      dataIndex: 'age',
      key: 'age',
      render: val => (val !== null && val !== undefined ? val : ''),
    },
    {
      title: 'Ngày tạo hồ sơ',
      dataIndex: 'createdDate',
      key: 'createdDate',
      render: val => val || '',
    },
  ];

  const relativeColumns: ColumnsType<ProfileDetailItem> = [
    {
      title: 'User ID Người tạo Hồ sơ',
      dataIndex: 'creatorUserId',
      key: 'creatorUserId',
      render: (val, record) => val || record.userId || '',
    },
    {
      title: 'Họ và Tên',
      dataIndex: 'fullName',
      key: 'fullName',
      render: val => val || '',
    },
    {
      title: 'Ngày sinh',
      dataIndex: 'dob',
      key: 'dob',
      render: val => val || '',
    },
    {
      title: 'Giới tính',
      dataIndex: 'gender',
      key: 'gender',
      render: val => val || '',
    },
    {
      title: 'Độ tuổi',
      dataIndex: 'age',
      key: 'age',
      render: val => (val !== null && val !== undefined ? val : ''),
    },
    {
      title: 'Ngày tạo hồ sơ',
      dataIndex: 'createdDate',
      key: 'createdDate',
      render: val => val || '',
    },
  ];

  const sharedDetailColumns: ColumnsType<ProfileDetailItem> = [
    {
      title: 'User ID',
      dataIndex: 'userId',
      key: 'userId',
      render: val => val || '',
    },
    {
      title: 'User ID Người tạo Hồ sơ',
      dataIndex: 'creatorUserId',
      key: 'creatorUserId',
      render: (val, record) => val || record.userId || '',
    },
    {
      title: 'Họ và Tên',
      dataIndex: 'fullName',
      key: 'fullName',
      render: val => val || '',
    },
    {
      title: 'Số điện thoại',
      dataIndex: 'phone',
      key: 'phone',
      render: val => val || '',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: val => val || '',
    },
    {
      title: 'Ngày sinh',
      dataIndex: 'dob',
      key: 'dob',
      render: val => val || '',
    },
    {
      title: 'Giới tính',
      dataIndex: 'gender',
      key: 'gender',
      render: val => val || '',
    },
    {
      title: 'Tỉnh thành',
      dataIndex: 'province',
      key: 'province',
      render: val => val || '',
    },
    {
      title: 'Độ tuổi',
      dataIndex: 'age',
      key: 'age',
      render: val => (val !== null && val !== undefined ? val : ''),
    },
    {
      title: 'Ngày tạo hồ sơ',
      dataIndex: 'createdDate',
      key: 'createdDate',
      render: val => val || '',
    },
  ];

  let columns = userColumns;
  if (target === 'relative') {
    columns = relativeColumns;
  } else if (target === 'all') {
    columns = sharedDetailColumns;
  }

  return (
    <Card css={cardStyle}>
      <div css={headerRowStyle}>
        <Title level={4} style={{ margin: 0 }}>
          Danh sách chi tiết
        </Title>
      </div>
      <div css={headerRowStyle}>
        <Text type="secondary" style={{ fontSize: 14 }}>
          Tổng số: <b>{totalRows.toLocaleString('vi-VN')}</b> kết quả
        </Text>
      </div>
      <Table
        rowKey={(rec, idx) => `${rec.userId || idx}_${idx}`}
        columns={columns}
        dataSource={data}
        loading={loading}
        scroll={{ x: 'max-content' }}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: totalRows,
          onChange: onPageChange,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          locale: { items_per_page: '/ trang' },
        }}
      />
    </Card>
  );
};

const cardStyle = css`
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
`;

const headerRowStyle = css`
  margin-bottom: 16px;
`;
